import React from "react"
import { graphql } from "gatsby"
import {
  Container,
  Box,
  Heading,
  Text,
  Flex,
  Grid,
  Image,
} from "@theme-ui/components"
import Layout from "../components/layout"
import ArrowPrimary from "../images/list-arrow-violet.svg"
import { getBlogPath } from "../utils/path"
import CategoriesList from "./categoriesList"
import BlogTitle from "../components/blogTitle"
import { HelmetDatoCms } from "gatsby-source-datocms"
import { GatsbyImage } from "gatsby-plugin-image"
import CtaCollection from "../components/ctaCollection"
import { InboundLink } from "../components/link"
import { getArticlePath, getPagePath } from "../utils/path"
import { LanguageSwitcherContext } from "../context/languageSwitcherContext"
import { i18nContext } from "../context/i18nContext"
import { motion } from "framer-motion"

const Blog = ({
  data: { page, articles, articleCategories, site, footer, menu, partner },
  pageContext,
}) => {
  console.log(articles)
  const locale = pageContext.locale
  const i18nPaths = site.locales.map(locale => {
    return {
      locale: locale,
      value: getBlogPath(locale),
    }
  })

  // State for managing visible articles
  const [visibleCount, setVisibleCount] = React.useState(6)

  // Function to format date as DD.MM.YYYY
  const formatDate = dateString => {
    const date = new Date(dateString)
    const day = date.getDate().toString().padStart(2, "0")
    const month = (date.getMonth() + 1).toString().padStart(2, "0")
    const year = date.getFullYear()
    return `${day}.${month}.${year}`
  }

  // Function to load more articles
  const loadMore = () => {
    setVisibleCount(prevCount => prevCount + 6)
  }

  // Separate first article (hero) from the rest
  const heroArticle = articles.nodes[0]
  const remainingArticles = articles.nodes.slice(1)
  const visibleArticles = remainingArticles.slice(0, visibleCount)
  const hasMoreArticles = remainingArticles.length > visibleCount

  return (
    <Layout
      locale={locale}
      i18nPaths={i18nPaths}
      footerData={footer.nodes}
      menuData={menu.nodes}
      partner={partner}
    >
      <HelmetDatoCms seo={page.seoMetaTags}>
        <html lang={locale} />
      </HelmetDatoCms>

      {/* Hero Article Section */}
      {heroArticle && (
        <ArticleHero article={heroArticle} formatDate={formatDate} />
      )}

      {/* Remaining Articles as Cards */}
      {remainingArticles.length > 0 && (
        <Container sx={{ mt: [3, 5, 6, 6], padding: ["16px", "16px", 0, 0] }}>
          <Heading
            variant="kicker"
            sx={{
              fontSize: ["18px"],
              fontWeight: 500,
              color: "primary",
              textTransform: "uppercase",
              display: ["block", "block", "none", "none"],
              mb: "40px",
            }}
          >
            Altri Articoli
          </Heading>
          <Grid
            columns={[1, 2, 3]}
            sx={{
              gap: ["30px"],
              rowGap: ["90px"],
              alignItems: "stretch",
              a: {
                textDecoration: "none",
              },
            }}
          >
            {visibleArticles.map((article, index) => (
              <ArticleCard
                key={article.id}
                article={article}
                formatDate={formatDate}
              />
            ))}
          </Grid>

          {/* Load More Button */}
          {hasMoreArticles && (
            <i18nContext.Consumer>
              {t => (
                <Box
                  sx={{
                    mt: [4, 5, 6],
                    display: "flex",
                  }}
                >
                  <Flex
                    sx={{
                      button: {
                        display: "flex",
                        alignItems: "center",
                        fontFamily: "mono",
                        backgroundColor: "primary",
                        borderRadius: "40px",
                        color: "light",
                        textTransform: "uppercase",
                        fontWeight: 500,
                        fontSize: [2, 2, 2, "16px"],
                        p: 3,
                        px: 4,
                        border: "none",
                        cursor: "pointer",
                        ":hover": {
                          backgroundColor: "secondary",
                        },
                      },
                    }}
                  >
                    <button onClick={loadMore}>
                      <Text>Scopri di più</Text>
                      <Flex sx={{ alignItems: "center" }}>
                        <svg
                          width="30"
                          height="30"
                          viewBox="0 0 30 30"
                          fill="none"
                          style={{ marginLeft: "10px" }}
                        >
                          <path
                            d="M7 10L12 15L17 10"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </Flex>
                    </button>
                  </Flex>
                </Box>
              )}
            </i18nContext.Consumer>
          )}
        </Container>
      )}
      <Box>
        {page.content.map(
          block =>
            block.model.apiKey === "cta_collection" && (
              <CtaCollection block={block} />
            )
        )}
      </Box>
    </Layout>
  )
}

const ArticleHero = ({ article, formatDate }) => {
  const locale = React.useContext(LanguageSwitcherContext).activeLocale

  return (
    <i18nContext.Consumer>
      {t => (
        <Box>
          {/* MOBILE */}
          <Container
            sx={{
              mt: 5,
              display: ["block", "block", "none", "none"],
              padding: [3, 3, 3, 4],
            }}
          >
            <Text
              variant="kicker"
              sx={{ color: "primary", borderColor: "primary" }}
            >
              {formatDate(article.meta.firstPublishedAt)}
            </Text>
            <Heading
              sx={{
                mt: [4],
                fontSize: [5, "48px"],
                color: "primary",
                fontWeight: 500,
                width: ["100%", "100%", "50%"],
                lineHeight: 1.2,
                a: {
                  color: "primary",
                },
              }}
            >
              <InboundLink
                to={getArticlePath(article, locale)}
                sx={{ textDecoration: "none" }}
              >
                {article.title}
              </InboundLink>
            </Heading>
            <Box
              sx={{
                mt: [4],
                position: "relative",
                height: ["220px"],
              }}
            >
              <Box
                sx={{
                  borderRadius: ["30px", "60px"],
                  overflow: "hidden",
                  display: "flex",
                  height: "100%",
                  position: "relative",
                }}
              >
                {article.heroImage && (
                  <GatsbyImage
                    image={article.heroImage.gatsbyImageData}
                    alt={article.title}
                    style={{ height: "100%", width: "100%" }}
                  />
                )}
                <Box
                  sx={{
                    position: "absolute",
                    bottom: "20px",
                    left: "20px",
                    right: "20px",
                    zIndex: 2,
                  }}
                >
                  <InboundLink
                    to={getArticlePath(article, locale)}
                    sx={{
                      display: "inline-block",
                      fontFamily: "mono",
                      backgroundColor: "primary",
                      borderRadius: "40px",
                      color: "white",
                      textTransform: "uppercase",
                      fontWeight: 500,
                      fontSize: "14px",
                      p: 2,
                      px: 3,
                      textDecoration: "none",
                      ":hover": {
                        backgroundColor: "secondary",
                      },
                    }}
                  >
                    {t.discoverMore}
                  </InboundLink>
                </Box>
              </Box>
            </Box>
          </Container>

          {/* DESKTOP */}
          <Container
            sx={{
              mt: [7, 7, 7, 9],
              position: "relative",
              display: ["none", "none", "block", "block"],
            }}
          >
            <Box
              sx={{
                overflow: "hidden",
                height: ["640px"],
                ".gatsby-image-wrapper": {
                  borderRadius: "80px",
                },
              }}
            >
              {article.heroImage && (
                <GatsbyImage
                  image={article.heroImage.gatsbyImageData}
                  alt={article.title}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    zIndex: 1,
                  }}
                />
              )}
              <Flex
                sx={{
                  position: "relative",
                  padding: "45px",
                  width: "100%",
                  height: "100%",
                  zIndex: 2,
                  fontSize: "15px",

                  justifyContent: "space-between",
                  flexDirection: "column",
                }}
              >
                <Text
                  variant="kicker"
                  sx={{
                    color: "white",
                    borderColor: "white",
                    fontWeight: 500,
                    textTransform: "uppercase",
                  }}
                >
                  Blog
                </Text>
                <Flex
                  sx={{
                    flexDirection: "column",
                    gap: "15px",
                  }}
                >
                  <Text
                    sx={{
                      color: "white",
                      borderColor: "white",
                      fontWeight: 500,
                      lineHeight: 1.15,
                      fontSize: "16px",
                      textTransform: "uppercase",
                    }}
                  >
                    {formatDate(article.meta.firstPublishedAt)}
                  </Text>
                  {article.category && (
                    <InboundLink
                      to={getPagePath(article.category.link, locale)}
                      sx={{
                        textDecoration: "none",
                        color: "inherit",
                        lineHeight: 1.15,
                        fontSize: "16px",
                        color: "white",
                        borderColor: "white",
                        fontWeight: 500,
                        fontSize: "16px",
                        lineHeight: 1.15,
                        width: "fit-content",
                        color: "primary",
                        textTransform: "uppercase",
                        ":hover": {
                          textDecoration: "underline",
                          color: "primary",
                        },
                      }}
                    >
                      {article.category.title}
                    </InboundLink>
                  )}

                  <Heading
                    sx={{
                      fontSize: "48px",
                      color: "white",
                      fontWeight: 500,
                      lineHeight: 1.15,

                      width: ["100%", "100%", "100%", "50%"],
                      lineHeight: 1.2,
                    }}
                  >
                    {article.title}
                  </Heading>
                  <Box
                    sx={{
                      mt: ["60px"],
                      a: {
                        textAlign: ["start", "start", "center", "center"],
                        fontFamily: "mono",
                        backgroundColor: "primary",
                        borderRadius: "40px",
                        color: "white",
                        textTransform: "uppercase",
                        fontWeight: 500,
                        fontSize: [2, 2, 2, "16px"],
                        p: 3,
                        textDecoration: "none",
                        px: 4,
                        ":hover": {
                          backgroundColor: "secondary",
                        },
                      },
                    }}
                  >
                    <InboundLink
                      to={getArticlePath(article, locale)}
                      sx={{ textDecoration: "none", color: "white" }}
                    >
                      {t.discoverMore}
                    </InboundLink>
                  </Box>
                </Flex>
              </Flex>
            </Box>
          </Container>
        </Box>
      )}
    </i18nContext.Consumer>
  )
}

const ArticleCard = ({ article, formatDate }) => {
  const locale = React.useContext(LanguageSwitcherContext).activeLocale

  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      transition={{ ease: "easeInOut", duration: 0.2 }}
      style={{ height: "100%" }}
    >
      <InboundLink
        to={getArticlePath(article, locale)}
        sx={{
          textDecoration: "none",
          height: "100%",
          display: "flex",
          ":hover": {
            ".arrow-primary": {
              transform: "scaleX(1.4)",
            },
            ".cta-text": {
              transform: "translateX(20px)",
            },
          },
        }}
      >
        <Flex
          sx={{
            flexDirection: "column",
            justifyContent: "space-between",
            alignContent: "center",
            alignItems: "stretch",
            height: "100%",
            flex: 1,
          }}
        >
          <Box
            sx={{
              height: "300px",
              overflow: "hidden",
              width: "100%",
              borderRadius: "80px",
              flexShrink: 0,

              ".gatsby-image-wrapper": {
                minHeight: "300px",
                minWidth: "100%",
                height: "100%",
              },
            }}
          >
            {article.heroImage && (
              <GatsbyImage
                image={article.heroImage.gatsbyImageData}
                alt={article.title}
              />
            )}
          </Box>
          <Flex
            sx={{
              mt: "30px",
              gap: "15px",
              flexDirection: "column",
              alignItems: "stretch",
              flex: 1,
              justifyContent: "space-between",
            }}
          >
            <Flex
              sx={{
                flexDirection: "column",
                gap: "15px",
                flex: 1,
              }}
            >
              <Text
                sx={{
                  fontSize: 1,
                  textTransform: "uppercase",
                  lineHeight: 1.15,
                  color: "dark",
                  fontWeight: 500,
                  fontFamily: "mono",
                }}
              >
                {formatDate(article.meta.firstPublishedAt)}
              </Text>
              {article.category && (
                <Text
                  sx={{
                    fontSize: 1,
                    lineHeight: 1.15,
                    textTransform: "uppercase",
                    color: "primary",
                    fontWeight: 500,
                    fontFamily: "mono",
                  }}
                >
                  {article.category.title}
                </Text>
              )}
              <Heading
                as="h2"
                sx={{
                  mt: [2, 2, 3],
                  fontSize: 1,
                  lineHeight: 1.5,
                  textTransform: "uppercase",
                  color: "dark",
                  fontWeight: 500,
                  fontFamily: "mono",
                  flex: 1,
                }}
              >
                {article.title}
              </Heading>
            </Flex>
            <Flex
              sx={{
                gap: "10px",
                alignItems: "center",
                flexShrink: 0,
              }}
            >
              <Image
                className="arrow-primary"
                sx={{
                  width: "50px",
                  height: "auto",
                  transition: "transform 0.3s ease",
                  transformOrigin: "left center",
                }}
                src={ArrowPrimary}
              />
              <Heading
                className="cta-text"
                sx={{
                  fontWeight: 500,
                  fontSize: [3, 3, 2, "16px"],
                  transition: "transform 0.3s ease",
                }}
              >
                Leggi di più
              </Heading>
            </Flex>
          </Flex>
        </Flex>
      </InboundLink>
    </motion.div>
  )
}

export default Blog

export const query = graphql`
  query BlogQuery($locale: String!) {
    page: datoCmsBlogPage(locale: $locale) {
      ...BlogDetails

      content {
        ... on DatoCmsCtaCollection {
          id
          cta {
            title
            subtitle
            link {
              anchor
              link {
                ... on DatoCmsPage {
                  root
                  id
                  _allSlugLocales {
                    locale
                    value
                  }
                }
              }
            }
          }
          model {
            apiKey
          }
        }
      }
      seoMetaTags {
        ...GatsbyDatoCmsSeoMetaTags
      }
    }
    partner: allDatoCmsPartner(sort: { position: ASC }) {
      ...PartnerFragment
    }

    menu: allDatoCmsMenu(
      locale: $locale
      filter: { root: { eq: true }, locales: { eq: $locale } }
      sort: { position: ASC }
    ) {
      nodes {
        ...MenuDetails
      }
    }

    articles: allDatoCmsArticle(
      locale: $locale
      sort: { meta: { firstPublishedAt: DESC } }
      filter: { locales: { eq: $locale } }
    ) {
      nodes {
        ...ArticleDetails
        ...ArticleAllSlugLocales
        meta {
          firstPublishedAt
        }
      }
    }
    articleCategories: allDatoCmsArticleCategory(
      locale: $locale
      sort: { position: ASC }
      filter: { locales: { eq: $locale } }
    ) {
      nodes {
        ...ArticleCategoryDetails
        ...ArticleCategoryAllSlugLocales
      }
    }

    site: datoCmsSite {
      locales
    }

    footer: allDatoCmsFooter(
      locale: $locale
      filter: { root: { eq: true }, locales: { eq: $locale } }
      sort: { position: ASC }
    ) {
      nodes {
        ...FooterDetails
      }
    }
  }

  fragment BlogDetails on DatoCmsBlogPage {
    id
    locales
    title
    model {
      apiKey
    }
  }
`
